import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatedSection } from '../../../components/AnimatedSection';

type FilterId = 'all' | 'retail' | 'finance' | 'healthcare';

export default function CaseStudiesGrid() {
  const { t } = useTranslation();
  const [activeFilter, setActiveFilter] = useState<FilterId>('all');

  const filters: { id: FilterId; label: string }[] = [
    { id: 'all', label: t('cs_filter_all') },
    { id: 'retail', label: t('cs_filter_retail') },
    { id: 'finance', label: t('cs_filter_finance') },
    { id: 'healthcare', label: t('cs_filter_healthcare') },
  ];

  const caseStudies = [
    {
      industryId: 'retail' as FilterId,
      industry: t('cs_c1_industry'),
      company: t('cs_c1_company'),
      img: 'https://public.readdy.ai/ai/img_res/df1ce90e65cd2a393cc0bb8123abc5ee.jpg',
      challenge: t('cs_c1_challenge'),
      solution: t('cs_c1_solution'),
      results: [
        { metric: t('cs_c1_r1_metric'), label: t('cs_c1_r1_label') },
        { metric: t('cs_c1_r2_metric'), label: t('cs_c1_r2_label') },
        { metric: t('cs_c1_r3_metric'), label: t('cs_c1_r3_label') },
      ],
      tags: [t('cs_c1_t1'), t('cs_c1_t2'), t('cs_c1_t3')],
    },
    {
      industryId: 'finance' as FilterId,
      industry: t('cs_c2_industry'),
      company: t('cs_c2_company'),
      img: 'https://readdy.ai/api/search-image?query=Financial%20office%20cybersecurity%20control%20center%20with%20multiple%20monitors%20showing%20firewall%20dashboards%20security%20alerts%20network%20monitoring%20system%20dark%20themed%20professional%20enterprise%20security%20operations%20center&width=800&height=500&seq=cs-finance-002&orientation=landscape',
      challenge: t('cs_c2_challenge'),
      solution: t('cs_c2_solution'),
      results: [
        { metric: t('cs_c2_r1_metric'), label: t('cs_c2_r1_label') },
        { metric: t('cs_c2_r2_metric'), label: t('cs_c2_r2_label') },
        { metric: t('cs_c2_r3_metric'), label: t('cs_c2_r3_label') },
      ],
      tags: [t('cs_c2_t1'), t('cs_c2_t2'), t('cs_c2_t3')],
    },
    {
      industryId: 'healthcare' as FilterId,
      industry: t('cs_c3_industry'),
      company: t('cs_c3_company'),
      img: 'https://readdy.ai/api/search-image?query=Medical%20clinic%20healthcare%20IT%20system%20patient%20management%20software%20screen%20showing%20appointment%20scheduling%20electronic%20health%20records%20medical%20data%20management%20professional%20healthcare%20technology%20secure%20system&width=800&height=500&seq=cs-medical-004&orientation=landscape',
      challenge: t('cs_c3_challenge'),
      solution: t('cs_c3_solution'),
      results: [
        { metric: t('cs_c3_r1_metric'), label: t('cs_c3_r1_label') },
        { metric: t('cs_c3_r2_metric'), label: t('cs_c3_r2_label') },
        { metric: t('cs_c3_r3_metric'), label: t('cs_c3_r3_label') },
      ],
      tags: [t('cs_c3_t1'), t('cs_c3_t2'), t('cs_c3_t3')],
    },
  ];

  const filtered = activeFilter === 'all'
    ? caseStudies
    : caseStudies.filter(c => c.industryId === activeFilter);

  return (
    <section className="py-20 bg-slate-900">
      <div className="max-w-7xl mx-auto px-6">
        <AnimatedSection animation="fade-in">
          <div className="flex flex-wrap justify-center gap-3 mb-14">
            {filters.map(f => (
              <button
                key={f.id}
                onClick={() => setActiveFilter(f.id)}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  activeFilter === f.id
                    ? 'bg-cyan-500 text-slate-900'
                    : 'bg-slate-800 text-slate-300 hover:bg-slate-700 border border-slate-700'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </AnimatedSection>

        <div className="space-y-12">
          {filtered.map((c, i) => (
            <AnimatedSection key={c.company} animation="slide-up" delay={i * 0.08}>
              <div className="bg-slate-800/50 border border-slate-700 hover:border-slate-600 rounded-2xl overflow-hidden transition-all">
                <div className="grid md:grid-cols-2 gap-0">
                  <div className="h-64 md:h-auto overflow-hidden">
                    <img src={c.img} alt={c.company} className="w-full h-full object-cover object-top" />
                  </div>
                  <div className="p-8 flex flex-col justify-center">
                    <span className="inline-block text-xs font-semibold px-3 py-1 rounded-full bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 mb-4 w-fit">
                      {c.industry}
                    </span>
                    <h3 className="text-xl font-bold text-white mb-4">{c.company}</h3>
                    <div className="mb-3">
                      <p className="text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wider">{t('cs_label_challenge')}</p>
                      <p className="text-slate-400 text-sm leading-relaxed">{c.challenge}</p>
                    </div>
                    <div className="mb-5">
                      <p className="text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wider">{t('cs_label_solution')}</p>
                      <p className="text-slate-400 text-sm leading-relaxed">{c.solution}</p>
                    </div>
                    <div className="grid grid-cols-3 gap-4 mb-5 pt-4 border-t border-slate-700">
                      {c.results.map(r => (
                        <div key={r.label} className="text-center">
                          <div className="text-xl font-bold text-cyan-400">{r.metric}</div>
                          <div className="text-slate-500 text-xs mt-0.5">{r.label}</div>
                        </div>
                      ))}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {c.tags.map(tag => (
                        <span key={tag} className="text-xs px-3 py-1 bg-slate-700 text-slate-300 rounded-full">{tag}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          ))}
        </div>
      </div>
    </section>
  );
}
